import { Button, Stack, Tooltip } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { openedStructureSetApprovalConfirmationDialogState } from '~/state/atoms/structureSet/roiAtoms';

interface StructureSetApprovalButtonsProps {
    dicomUid: string;
    approvalStatus: DICOMApprovalStatus;
    disabledReasonId?: string;
}

export default function StructureSetApprovalButtons({
    dicomUid,
    approvalStatus,
    disabledReasonId,
}: StructureSetApprovalButtonsProps) {
    const intl = useIntl();
    const setOpenedApprovalDialog = useSetRecoilState(
        openedStructureSetApprovalConfirmationDialogState
    );
    const decide = (newApprovalStatus: DICOMApprovalStatus) =>
        setOpenedApprovalDialog({ sopUid: dicomUid, newApprovalStatus });
    const isSignedOff =
        approvalStatus !== DICOMApprovalStatus.UNAPPROVED &&
        Object.values(DICOMApprovalStatus).includes(approvalStatus);
    const blocked = !!disabledReasonId;

    return isSignedOff ? (
        <Button
            size="small"
            variant="text"
            sx={{ fontSize: 11, whiteSpace: 'nowrap' }}
            onClick={() => decide(DICOMApprovalStatus.UNAPPROVED)}
            data-testid="replanning-unapprove-structures"
        >
            <FormattedMessage id="replanning.structureSet.unapprove" />
        </Button>
    ) : (
        <Tooltip
            title={blocked ? intl.formatMessage({ id: disabledReasonId }) : ''}
        >
            <Stack spacing={1} direction="row">
                <Button
                    size="small"
                    variant="contained"
                    color="warning"
                    disableElevation
                    disabled={blocked}
                    sx={{ fontSize: 11, whiteSpace: 'nowrap' }}
                    onClick={() => decide(DICOMApprovalStatus.APPROVED)}
                    data-testid="replanning-approve-structures"
                >
                    <FormattedMessage id="replanning.structureSet.approve" />
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={blocked}
                    sx={{ fontSize: 11, whiteSpace: 'nowrap' }}
                    onClick={() => decide(DICOMApprovalStatus.REJECTED)}
                    data-testid="replanning-reject-structures"
                >
                    <FormattedMessage id="replanning.structureSet.reject" />
                </Button>
            </Stack>
        </Tooltip>
    );
}
